/**
 * ZAdminOrganizations - Zod Validation Schemas for Admin Organizations API
 *
 * Single source of truth for Admin Organizations domain API validation.
 * These schemas generate both runtime validation (Zod) and compile-time types (TApi).
 *
 * Naming: Z prefix = Zod schemas (vs TApi = TypeScript types, DbEntity = Prisma)
 *
 * Admin-panel endpoints, protected by AdminPermissionGuard (PBAC).
 * These endpoints manage synchronization between Clerk (auth provider) and Nexus DB.
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";

// ═══════════════════════════════════════════════════════════════
// SHARED SCHEMAS - Reusable pieces
// ═══════════════════════════════════════════════════════════════

/**
 * Clerk Organization ID validation
 * Format: org_XXXXXXXXXXXXXXXXXXXXXXXXXXXXX (Clerk's org ID format)
 */
const ClerkOrgIdSchema = z
  .string()
  .min(1, "Clerk organization ID is required")
  .regex(/^org_/, "Clerk organization ID must start with 'org_'");

/**
 * Organization sync status enum
 * - "synced": Org exists in both Clerk and DB with matching names
 * - "missing": Org exists in Clerk but not in DB
 * - "mismatch": Org exists in both but names don't match
 */
const OrgSyncStatusSchema = z.enum(["synced", "missing", "mismatch"]);

// ═══════════════════════════════════════════════════════════════
// RESPONSE SCHEMAS - Output validation
// ═══════════════════════════════════════════════════════════════

/**
 * Individual organization sync information
 */
export const OrgSyncInfoSchema = z.object({
  /** Clerk organization ID */
  clerkId: z.string(),
  /** Organization name in Clerk */
  clerkName: z.string(),
  /** Organization slug in Clerk (URL-friendly) */
  clerkSlug: z.string().nullable(),
  /** Whether the org exists in Nexus DB */
  inDb: z.boolean(),
  /** Organization name in Nexus DB (null if not in DB) */
  dbName: z.string().nullable(),
  /** Current sync status */
  status: OrgSyncStatusSchema,
  /** Number of members in Clerk */
  membersCount: z.number().int().min(0),
  /** Number of OrganizationMembership rows in Nexus DB */
  dbMembersCount: z.number().int().min(0),
  /**
   * True when the org exists in the DB with zero members while Clerk reports
   * members — the NEX-2398 symptom. These orgs are repairable by re-syncing.
   */
  memberless: z.boolean(),
  /**
   * True when the org holds SOME members but not the same number Clerk reports.
   *
   * `memberless` is the total case and could only ever see an org at zero, so an
   * org mirroring 30 of 32 members read as fully synced. That gap is not
   * cosmetic: a member Clerk has and we do not is invisible everywhere until a
   * group write names them, and then it refuses the whole write. Partial drift is
   * the common shape — one webhook missed, not all of them.
   *
   * Repaired by the same re-sync where we hold FEWER rows than Clerk. The other
   * direction — rows Clerk no longer reports — is detected here and reported as
   * a failed repair, because the re-sync only adds rows from Clerk and removing
   * one revokes a person's access. Undefined member counts from Clerk are NOT
   * drift — see `memberCountKnown`.
   */
  memberDrift: z.boolean(),
  /**
   * Whether Clerk actually reported a member count for this org.
   *
   * `membersCount` is coerced to 0 when Clerk omits it, and 0 is a real count —
   * so without this flag every org would read as drifting the moment the count
   * failed to arrive. Drift is only ever computed where this is true.
   */
  memberCountKnown: z.boolean(),
  /** When the org was created in Clerk (ISO 8601) */
  createdAt: z.string().datetime()
});

/**
 * Summary statistics for sync status
 */
export const SyncSummarySchema = z.object({
  /** Total organizations in Clerk */
  clerkTotal: z.number().int().min(0),
  /** Total organizations in Nexus DB */
  dbTotal: z.number().int().min(0),
  /** Organizations that are fully synced */
  synced: z.number().int().min(0),
  /** Organizations missing from Nexus DB */
  missing: z.number().int().min(0),
  /** Organizations with name mismatches */
  nameMismatch: z.number().int().min(0),
  /** Organizations present in DB but with zero members while Clerk has some (NEX-2398) */
  memberless: z.number().int().min(0),
  /** Organizations mirroring some but not all of the members Clerk reports */
  memberDrift: z.number().int().min(0)
});

/**
 * GET /sync-status - Complete sync status response
 */
export const SyncStatusResponseSchema = z.object({
  summary: SyncSummarySchema,
  organizations: z.array(OrgSyncInfoSchema)
});

/**
 * POST /sync/:clerkOrgId - Single organization sync response
 */
export const SyncOrgResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  organization: z.object({
    id: z.string(),
    name: z.string()
  })
});

/**
 * Organization billing information for admin panel
 */
export const OrgBillingInfoSchema = z.object({
  /** Organization ID */
  id: z.string(),
  /** Organization name */
  name: z.string(),
  /** Subscription tier (PAY_AS_YOU_GO, HOBBY, PRO, ENTERPRISE) */
  subscriptionType: z.enum(["PAY_AS_YOU_GO", "HOBBY", "PRO", "ENTERPRISE"]).nullable(),
  /** Current balance in USD (converted from ten-thousandths) */
  balance: z.number(),
  /** Balance earned in USD */
  balanceEarned: z.number(),
  /** Balance from subscription in USD */
  balanceFromSub: z.number(),
  /** Amount to charge in USD */
  amountToCharge: z.number(),
  /** Whether automatic top-up is enabled */
  automaticTopUp: z.boolean(),
  /** Stripe customer ID (if linked) */
  stripeCustomerId: z.string().nullable(),
  /** Number of active deployments */
  deploymentsCount: z.number().int().min(0),
  /** Number of organization members */
  membersCount: z.number().int().min(0),
  /** When the org was created */
  createdAt: z.string().datetime()
});

/**
 * Billing overview summary statistics
 */
export const BillingSummarySchema = z.object({
  /** Total organizations */
  totalOrgs: z.number().int().min(0),
  /** Total balance across all orgs (USD) */
  totalBalance: z.number(),
  /** Organizations with Stripe linked */
  stripeLinked: z.number().int().min(0),
  /** Organizations with auto top-up enabled */
  autoTopUpEnabled: z.number().int().min(0),
  /** Total amount to be charged (USD) */
  totalToCharge: z.number()
});

/**
 * GET /billing-overview - Query params for the paginated org billing table.
 *
 * `search` matches org name (case-insensitive) or exact/partial id. The
 * summary block is always computed across ALL orgs regardless of these
 * params — only the `organizations` page and `total` respect them.
 */
export const BillingOverviewQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  search: z.string().optional()
});

/**
 * GET /billing-overview - Complete billing overview response
 *
 * `summary` is org-wide (unfiltered). `organizations` is a single page of
 * the (optionally searched) list; `total` is the filtered row count for
 * pagination.
 */
export const BillingOverviewResponseSchema = z.object({
  summary: BillingSummarySchema,
  organizations: z.array(OrgBillingInfoSchema),
  total: z.number().int().min(0),
  page: z.number().int().min(1),
  limit: z.number().int().min(1)
});

/**
 * PATCH /:id/billing - Update organization billing settings
 */
export const UpdateBillingBodySchema = z.object({
  /** New subscription type */
  subscriptionType: z.enum(["PAY_AS_YOU_GO", "HOBBY", "PRO", "ENTERPRISE"]).optional(),
  /** Enable/disable automatic top-up */
  automaticTopUp: z.boolean().optional(),
  /** Custom max workflow iterations per execution (null = use global default from WORKFLOW_MAX_ITERATIONS env var, default 50000) */
  customWorkflowMaxIterations: z.number().int().min(1).nullable().optional()
});

export const UpdateBillingResponseSchema = z.object({
  success: z.boolean(),
  message: z.string()
});

/**
 * PATCH /:id/billing - Path variables (the organization id)
 */
export const UpdateBillingPathVarsSchema = z.object({
  id: z.string().min(1)
});

/**
 * GET /:orgId/detail - Full organization detail for admin overview
 */
export const OrgDetailResponseSchema = z.object({
  // Identity
  id: z.string(),
  name: z.string(),
  // Billing (raw ten-thousandths — frontend converts)
  balanceInUSDTenThousandths: z.number().int(),
  balanceEarnedInUSDTenThousandths: z.number().int(),
  balanceFromSubInUSDTenThousandths: z.number().int(),
  amountToChargeInUSDTenThousandths: z.number().int(),
  automaticTopUp: z.boolean(),
  stripeCustomerId: z.string().nullable(),
  // Configuration
  documentOwnershipOrder: z.number().int(),
  pineconeNamespace: z.string().nullable(),
  customRateLimitNodes: z.number().int().nullable(),
  customWorkflowMaxIterations: z.number().int().nullable(),
  // Raw JSON blobs (nullable)
  metadata: z.unknown().nullable(),
  echoConfig: z.unknown().nullable(),
  // Subscription (latest)
  subscriptionType: z.enum(["PAY_AS_YOU_GO", "HOBBY", "PRO", "ENTERPRISE"]).nullable(),
  subscriptionStatus: z.string().nullable(),
  // Counts
  deploymentsCount: z.number().int(),
  membersCount: z.number().int(),
  apiKeyConnectionsCount: z.number().int(),
  oauthConnectionsCount: z.number().int(),
  workflowsCount: z.number().int(),
  phoneNumbersCount: z.number().int()
});

export const OrgDetailPathVarsSchema = z.object({
  orgId: z.string().min(1)
});

/**
 * Single org row in the paginated list (DB-only data, no Clerk)
 */
export const OrgListItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  subscriptionType: z.enum(["PAY_AS_YOU_GO", "HOBBY", "PRO", "ENTERPRISE"]).nullable(),
  balance: z.number(),
  deploymentsCount: z.number().int(),
  membersCount: z.number().int()
});

/**
 * GET / - Query params for paginated org list
 */
export const ListOrgsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  search: z.string().optional(),
  subscriptionType: z.enum(["PAY_AS_YOU_GO", "HOBBY", "PRO", "ENTERPRISE"]).optional()
});

/**
 * GET / - Paginated orgs response
 */
export const ListOrgsResponseSchema = z.object({
  organizations: z.array(OrgListItemSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int()
});

// ═══════════════════════════════════════════════════════════════
// REQUEST SCHEMAS - Input validation
// ═══════════════════════════════════════════════════════════════

/**
 * POST /sync/:clerkOrgId - Path variables
 */
export const SyncOrgPathVarsSchema = z.object({
  clerkOrgId: ClerkOrgIdSchema
});

/**
 * DELETE /:orgId - Delete response
 *
 * deletedAt:   ISO timestamp of DB delete completion
 * backupKey:   S3 KEY of the ZIP archive taken before deletion, or null when the
 *              caller opted out via `?backup=false` (see DeleteOrganizationQuerySchema).
 *              DURABLE — this is the field to persist. The archive is uploaded
 *              `private`, so the key stays valid forever while a URL does not.
 * backupUrl:   a SHORT-LIVED presigned GET for that key, minted per response, or
 *              null when there is no key. It expires in minutes, so it is for
 *              handing to a browser and must never be stored or audited.
 * stepsFailed: names of cleanup steps that failed (empty = perfect run)
 */
export const DeleteOrganizationResponseSchema = z.object({
  deletedAt: z.string().datetime(),
  backupKey: z.string().nullable(),
  backupUrl: z.string().url().nullable(),
  stepsFailed: z.array(z.string())
});

/**
 * DELETE /:orgId - query
 *
 * backup: take a ZIP archive to S3 before deleting. Defaults to TRUE, and the
 *   default is the point: an omitted or malformed param must never silently mean
 *   "no backup" on an irreversible operation.
 *
 * Opting out is for bulk deletion of dead organizations, where thousands of
 * near-empty archives buy nothing. The admin panel does not pass it, so a
 * one-off delete through the UI always keeps its downloadable backup.
 */
export const DeleteOrganizationQuerySchema = z.object({
  // `z.coerce.boolean()` is unsafe for query strings — `Boolean("false") === true`,
  // which would turn `?backup=false` into a backup and, worse, make any typo read
  // as `true` rather than fail. Preprocess explicitly: only the exact strings
  // "true"/"false" are accepted, anything else is a 400 rather than a guess.
  backup: z.preprocess(
    (v) => (v === "true" ? true : v === "false" ? false : v),
    z.boolean().default(true)
  )
});

export const DeleteOrganizationPathVarsSchema = z.object({
  orgId: z.string().min(1)
});

// ═══════════════════════════════════════════════════════════════
// ZADMINORGANIZATIONS MANIFEST - Export all schemas for runtime use
// ═══════════════════════════════════════════════════════════════

/**
 * ZAdminOrganizations - Zod API validation schemas
 *
 * Single source of truth for Admin Organizations API:
 * - Runtime validation (backend + frontend)
 * - Type generation (TApi auto-inferred via InferSchemas)
 *
 * Z prefix = Zod schemas (vs DbEntity = Prisma, TApi = TypeScript)
 */
export const ZAdminOrganizations = {
  /**
   * List organizations from DB with pagination and search
   * GET /api/admin/organizations
   */
  ListOrganizations: {
    method: "GET" as const,
    path: "/api/admin/organizations",
    Query: ListOrgsQuerySchema,
    Response: ApiSuccessSchema(ListOrgsResponseSchema)
  },

  /**
   * Get sync status between Clerk and Nexus DB
   * GET /api/admin/organizations/sync-status
   */
  GetSyncStatus: {
    method: "GET" as const,
    path: "/api/admin/organizations/sync-status",
    Response: ApiSuccessSchema(SyncStatusResponseSchema)
  },

  /**
   * Sync a single organization from Clerk
   * POST /api/admin/organizations/sync/:clerkOrgId
   */
  SyncOrg: {
    method: "POST" as const,
    path: "/api/admin/organizations/sync/:clerkOrgId",
    PathVars: SyncOrgPathVarsSchema,
    Response: ApiSuccessSchema(SyncOrgResponseSchema)
  },

  /**
   * Get billing overview for all organizations
   * GET /api/admin/organizations/billing-overview
   */
  GetBillingOverview: {
    method: "GET" as const,
    path: "/api/admin/organizations/billing-overview",
    Query: BillingOverviewQuerySchema,
    Response: ApiSuccessSchema(BillingOverviewResponseSchema)
  },

  /**
   * Update organization billing settings
   * PATCH /api/admin/organizations/:id/billing
   */
  UpdateBilling: {
    method: "PATCH" as const,
    path: "/api/admin/organizations/:id/billing",
    PathVars: UpdateBillingPathVarsSchema,
    Body: UpdateBillingBodySchema,
    Response: ApiSuccessSchema(UpdateBillingResponseSchema)
  },

  /**
   * Get full organization detail for admin overview
   * GET /api/admin/organizations/:orgId/detail
   */
  GetOrgDetail: {
    method: "GET" as const,
    path: "/api/admin/organizations/:orgId/detail",
    PathVars: OrgDetailPathVarsSchema,
    Response: ApiSuccessSchema(OrgDetailResponseSchema)
  },

  /**
   * Delete an organization and all its external resources
   * DELETE /api/admin/organizations/:orgId
   *
   * Runs the full 8-step pipeline:
   *   1. Export ZIP to S3 (required — aborts if fails)
   *   2–7. Cleanup external services (best-effort: Clerk, Twilio, Stripe, Pinecone, Webhooks, S3)
   *   8. DB cascade delete (always runs)
   *
   * Returns backup URL and list of any cleanup steps that failed.
   */
  Delete: {
    method: "DELETE" as const,
    path: "/api/admin/organizations/:orgId",
    PathVars: DeleteOrganizationPathVarsSchema,
    Query: DeleteOrganizationQuerySchema,
    Response: ApiSuccessSchema(DeleteOrganizationResponseSchema)
  }
} as const;

// ═══════════════════════════════════════════════════════════════
// TYPE EXPORTS - For convenience
// ═══════════════════════════════════════════════════════════════

/** Sync status for an individual organization */
export type OrgSyncStatus = z.infer<typeof OrgSyncStatusSchema>;

/** Individual organization sync information */
export type OrgSyncInfo = z.infer<typeof OrgSyncInfoSchema>;

/** Summary statistics for sync status */
export type SyncSummary = z.infer<typeof SyncSummarySchema>;

/** Complete sync status response */
export type SyncStatusResponse = z.infer<typeof SyncStatusResponseSchema>;

/** Single organization sync response */
export type SyncOrgResponse = z.infer<typeof SyncOrgResponseSchema>;

/** Path variables for sync single org */
export type SyncOrgPathVars = z.infer<typeof SyncOrgPathVarsSchema>;

/** Organization billing information */
export type OrgBillingInfo = z.infer<typeof OrgBillingInfoSchema>;

/** Billing overview summary */
export type BillingSummary = z.infer<typeof BillingSummarySchema>;

/** Query params for the paginated billing overview */
export type BillingOverviewQuery = z.infer<typeof BillingOverviewQuerySchema>;

/** Complete billing overview response */
export type BillingOverviewResponse = z.infer<typeof BillingOverviewResponseSchema>;

/** Update billing request body */
export type UpdateBillingBody = z.infer<typeof UpdateBillingBodySchema>;

/** Update billing response */
export type UpdateBillingResponse = z.infer<typeof UpdateBillingResponseSchema>;

/** Path variables for update billing */
export type UpdateBillingPathVars = z.infer<typeof UpdateBillingPathVarsSchema>;

/** Full organization detail for admin overview */
export type OrgDetailResponse = z.infer<typeof OrgDetailResponseSchema>;

/** Path variables for get org detail */
export type OrgDetailPathVars = z.infer<typeof OrgDetailPathVarsSchema>;

/** Single org row in the paginated list */
export type OrgListItem = z.infer<typeof OrgListItemSchema>;
/** Query params for paginated org list */
export type ListOrgsQuery = z.infer<typeof ListOrgsQuerySchema>;
/** Paginated orgs response */
export type ListOrgsResponse = z.infer<typeof ListOrgsResponseSchema>;

/** Delete organization response */
export type DeleteOrganizationResponse = z.infer<typeof DeleteOrganizationResponseSchema>;

/** Path variables for delete organization */
export type DeleteOrganizationPathVars = z.infer<typeof DeleteOrganizationPathVarsSchema>;
